import {
    Button,
    ButtonGroup,
    FDate,
    Flexer,
    Form,
    Header,
    Heading,
    Icon,
    IconLib,
    Input,
    Modal,
    Option,
    Options,
    Portal,
    Text,
    useStorage,
    View,
} from '@fold-dev/core'
import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router'
import { getDateSelectTimeFormat, getWeekday, logger } from '../../helpers'
import { useTasks } from '../../hooks/api.hooks'
import { CalendarDays, CalendarProvider, CalendarSchedule, convert24toAmPm, getWeekDates } from './'
import { NewTaskModal } from '../../modals/new-task.modal'
import { useAppStore } from '../../store/app'
import { TbX } from 'react-icons/tb'

export const FORCE_DISABLE_TASKS = 'FORCE_DISABLE_TASKS'

const MonthView = ({ events, date, workingHours }) => {
    const weekDays = useMemo(() => getWeekDates(date).map((day) => getWeekday(day)), [date])

    return (
        <>
            <View
                row
                flex={0}
                width="100%">
                {weekDays.map((weekDay, index) => {
                    return (
                        <Text
                            flex={1}
                            p="0.5rem"
                            fontWeight="bold"
                            textAlign="center"
                            key={index}>
                            {weekDay}
                        </Text>
                    )
                })}
            </View>
            <CalendarDays
                date={date}
                events={events}
            />
        </>
    )
}

const DayView = ({ events, date, workingHours }) => {
    const { custom, days, weekDays, wholeDayEvents, timeEvents } = useMemo(() => {
        const weekDates = [date]
        const custom = [weekDates]
        const weekDays = weekDates.map((day) => getWeekday(day))
        const days = weekDates.map((day) => ({ date: day, workingHours }))

        return {
            days,
            custom,
            weekDays,
            wholeDayEvents: events.filter((e) => e.wholeDay),
            timeEvents: events.filter((e) => !e.wholeDay && !!e.end),
        }
    }, [date, events, workingHours])

    return (
        <>
            <View
                row
                flex={0}
                p="0 0 0 var(--f-calendar-schedule-gutter-width)"
                width="100%">
                {weekDays.map((weekDay, index) => {
                    return (
                        <Text
                            flex={1}
                            p="0.5rem"
                            fontWeight="bold"
                            textAlign="center"
                            key={index}>
                            {weekDay} {date.toLocaleString('default', { day: 'numeric' })}
                        </Text>
                    )
                })}
            </View>
            <View
                height="fit-content"
                p="0 1px 0 var(--f-calendar-schedule-gutter-width)"
                width="100%"
                position="relative"
                zIndex={1}>
                <CalendarDays
                    noClamp
                    date={date}
                    events={wholeDayEvents}
                    custom={custom}
                    height="fit-content"
                />
            </View>
            <View
                width="100%"
                flex={1}
                position="relative"
                zIndex={0}
                m="-1px 0 0 0"
                style={{ overflowY: 'scroll' }}
                className="f-scrollbar">
                <CalendarSchedule
                    days={days}
                    events={timeEvents}
                />
            </View>
        </>
    )
}

const WeekView = ({ events, date, workingHours, weekLength }) => {
    const { custom, days, weekDays, wholeDayEvents, timeEvents } = useMemo(() => {
        const weekDates = getWeekDates(date, weekLength)
        const custom = [weekDates]
        const weekDays = weekDates.map((day) => getWeekday(day))
        const days = weekDates.map((day) => ({ date: day, workingHours }))

        return {
            days,
            custom,
            weekDays,
            wholeDayEvents: events.filter((e) => e.wholeDay),
            timeEvents: events.filter((e) => !e.wholeDay && !!e.end),
        }
    }, [date, events, workingHours])

    return (
        <>
            <View
                row
                flex={0}
                p="0 0 0 var(--f-calendar-schedule-gutter-width)"
                width="100%">
                {weekDays.map((weekDay, index) => {
                    return (
                        <Text
                            flex={1}
                            p="0.5rem"
                            fontWeight="bold"
                            textAlign="center"
                            key={index}>
                            {weekDay}
                        </Text>
                    )
                })}
            </View>
            <View
                height="fit-content"
                p="0 1px 0 var(--f-calendar-schedule-gutter-width)"
                width="100%"
                position="relative"
                zIndex={1}>
                <CalendarDays
                    noClamp
                    date={date}
                    events={wholeDayEvents}
                    custom={custom}
                    height="fit-content"
                />
            </View>
            <View
                width="100%"
                flex={1}
                position="relative"
                zIndex={0}
                m="-1px 0 0 0"
                style={{ overflowY: 'scroll' }}
                className="f-scrollbar">
                <CalendarSchedule
                    days={days}
                    events={timeEvents}
                />
            </View>
        </>
    )
}

export const Calendar = ({
    isSidebar,
    type,
    tasks,
    teamId,
    view,
    date,
    onForward,
    onBack,
    onToday,
    onViewChange,
    onTaskUpdate,
    onTaskUserAdd,
    onTaskUserDelete,
}) => {
    const params = useParams()
    const { workspaceId, projectId } = params
    const sidebar = useAppStore((state) => state.sidebar)
    const workspace = useAppStore((state) => state.workspace)
    const actions = useAppStore((state) => state.actions)
    const workingHours = useMemo(() => ({ start: workspace.workHoursStart, end: workspace.workHoursEnd }), [workspace])
    const tasksAPI = useTasks()
    const navigate = useNavigate()
    const events = useMemo(() => {
        return tasks
            .filter((task) => !!task.start)
            .map((task) => ({
                ...task,
                projectId,
                color: task.projectColor,
                teamId,
            }))
    }, [tasks])

    const handleEventUpdate = async (event) => {
        const { id, start, end, wholeDay } = event

        onTaskUpdate({
            id,
            start,
            end,
            wholeDay,
        })
    }

    const handleEventOpen = (event, e) => {
        if (type == 'calendar') {
            navigate(`/app/workspace/${workspaceId}/calendar/task/${event.id}`)
        } else {
            if (sidebar == 'calendar') {
                navigate(`/app/workspace/${workspaceId}/c/${projectId}/${params.view}/task/${event.id}`)
            } else {
                navigate(`/app/workspace/${workspaceId}/c/${projectId}/calendar/task/${event.id}`)
            }
        }
    }

    return (
        <View
            flex={1}
            height="100%"
            position="relative"
            className="calendar-header"
            style={{ overflow: 'hidden' }}
            width="100%"
            onMouseEnter={() => (window[FORCE_DISABLE_TASKS] = true)}
            onMouseLeave={() => (window[FORCE_DISABLE_TASKS] = false)}
            onMouseOut={() => (window[FORCE_DISABLE_TASKS] = false)}
            alignItems="flex-start">
            <View
                column
                width="100%"
                height="100%"
                position="relative"
                justifyContent="stretch"
                alignContent="stretch"
                alignItems="stretch">
                <View
                    gap="1rem"
                    row
                    position="relative"
                    zIndex={2}
                    width="100%"
                    className="calendar-heading"
                    m={isSidebar ? '0' : '1rem 0 -1px 0'}
                    p={isSidebar ? '0' : '0.75rem 0.75rem 0.75rem 1.5rem'}
                    style={{
                        borderTop: isSidebar ? undefined : '1px solid var(--f-color-border)',
                        borderBottom: isSidebar ? undefined : '1px solid var(--f-color-border)',
                    }}>
                    {isSidebar && (
                        <Header
                            bgToken="background"
                            height="var(---header-sidebar-height)"
                            p="0 1rem">
                            <View
                                width="100%"
                                position="relative"
                                row>
                                <Heading
                                    as="h5"
                                    style={{ textTransform: 'capitalize' }}>
                                    {sidebar == 'tasks' ? 'Tasks' : 'Calendar'}
                                </Heading>
                                <Flexer />
                                <ButtonGroup>
                                    <Button
                                        size="xs"
                                        bgToken="surface-strong"
                                        onClick={onBack}>
                                        <IconLib icon="chevron-left" />
                                    </Button>
                                    <Button
                                        size="xs"
                                        bgToken="surface-strong"
                                        onClick={onToday}>
                                        <span className="f-text sm">Today</span>
                                    </Button>
                                    <Button
                                        size="xs"
                                        bgToken="surface-strong"
                                        onClick={onForward}>
                                        <IconLib icon="chevron-right" />
                                    </Button>
                                </ButtonGroup>
                                <Button
                                    size="xs"
                                    m="0 0 0 0.25rem"
                                    bgToken="surface-strong"
                                    onClick={() => {
                                        actions.setSidebar(null)
                                        window[FORCE_DISABLE_TASKS] = false
                                    }}>
                                    <Icon
                                        icon={TbX}
                                        size="sm"
                                    />
                                </Button>
                            </View>
                        </Header>
                    )}
                    {!isSidebar && (
                        <>
                            <Heading
                                as="h5"
                                m="0 auto 0 0"
                                fontWeight="bold"
                                textAlign="center">
                                {date.toLocaleString('default', { month: 'long' })}
                            </Heading>
                            <ButtonGroup>
                                <Button
                                    size="sm"
                                    onClick={onBack}>
                                    <IconLib icon="chevron-left" />
                                </Button>
                                <Button
                                    size="sm"
                                    onClick={onToday}>
                                    Today
                                </Button>
                                <Button
                                    size="sm"
                                    onClick={onForward}>
                                    <IconLib icon="chevron-right" />
                                </Button>
                            </ButtonGroup>
                            <Options
                                size="sm"
                                shadow="none"
                                animated
                                selected={view}
                                onOptionChange={onViewChange}>
                                <Option>Day</Option>
                                <Option>Week</Option>
                                <Option>Month</Option>
                            </Options>
                        </>
                    )}
                </View>
                <CalendarProvider
                    hideDateLabels={view == 0}
                    scheduleTimeFormat={(start, end) =>
                        getDateSelectTimeFormat(start) + ' - ' + getDateSelectTimeFormat(end)
                    }
                    gutterFormat={convert24toAmPm}
                    onEventOpen={handleEventOpen}
                    onEventUpdate={handleEventUpdate}
                    onTaskUserAdd={onTaskUserAdd}
                    onTaskUserDelete={onTaskUserDelete}
                    onEventAdd={({ done, event }) => {
                        return (
                            <NewTaskModal
                                type={type == 'calendar' ? 'calendar' : 'project'}
                                teamId={teamId}
                                onDismiss={done}
                                defaultDate={event.start}
                                defaultDateEnd={event.end}
                                defaultWholeday={event.wholeDay}
                                selectSection={type != 'calendar'}
                                selectProject={type == 'calendar'}
                            />
                        )
                    }}>
                    {(view == 0 || isSidebar) && (
                        <DayView
                            date={date}
                            events={events}
                            workingHours={workingHours}
                        />
                    )}

                    {view == 1 && !isSidebar && (
                        <WeekView
                            date={date}
                            events={events}
                            workingHours={workingHours}
                            weekLength={workspace.weekLength}
                        />
                    )}

                    {view == 2 && !isSidebar && (
                        <MonthView
                            date={date}
                            events={events}
                            workingHours={workingHours}
                        />
                    )}
                </CalendarProvider>
            </View>
        </View>
    )
}
